import React, { memo, useCallback, useContext, useEffect, useMemo, useState } from 'react'
import Tippy, { useSingleton } from '@tippyjs/react'
import type { Instance as TippyInstance } from 'tippy.js'
import { useTranslation } from 'react-i18next'
import { PredictionBars } from '@components/Survey/TabbedPane/PredictionBars'
import { SurveyContext } from '@context/SurveyContext'
import gotchiIcon from '@assets/images/characters/gotchi.png'
import robotIcon from '@assets/images/characters/robot.png'
import { DefiDatagotchiCallout } from '@components/DefiDatagotchiCallout/DefiDatagotchiCallout'
import gotchiMap from '@assets/images/misc/gotchi-map.png'
import gotchiQuestion from '@assets/images/misc/gotchi-question.png'
import bestGotchi from '@assets/images/home/best-gotchi.png'
import robotGotchi from '@assets/images/characters/robot-gotchi.gif'
import { PredictionResults } from '@models/Prediction/Prediction'
import { SurveyStep } from '@models/Survey/SurveyStep'
import {
  categorySurveyDisplayName,
  categorySurveyQuestionCount,
  getCategory,
  sortedCategorySurveys,
} from '@utils/categoryFlow'
import {
  buildEditionProgressSegments,
  ProgressPageId,
  ProgressSegment,
} from '@utils/editionProgress'
import 'tippy.js/dist/tippy.css'
import '@components/Survey/TabbedPane/EditionProgressBar.scss'
import './ConsentProgressDemo.scss'

function tippyBoundary(): Element {
  return document.querySelector('.AppLayout') ?? document.querySelector('.Consent') ?? document.body
}

function segmentTypeClass(segment: ProgressSegment): string {
  if (segment.kind === 'questionnaire') {
    return 'EditionProgressBar-segment--type-questionnaire'
  }
  return `EditionProgressBar-segment--type-${segment.pageId ?? 'consent'}`
}

function stepBlurbKey(segment: ProgressSegment): string {
  if (segment.kind === 'questionnaire') {
    return 'questionnaire'
  }
  if (segment.pageId === 'results') {
    return segment.resultVariant === 'intermediate' ? 'resultsIntermediate' : 'resultsFinal'
  }
  return segment.pageId ?? 'consent'
}

const STEP_IMAGES: Partial<Record<ProgressPageId, string>> = {
  email: gotchiMap,
  acknowledge: gotchiQuestion,
  prediction: bestGotchi,
  rectify: gotchiQuestion,
  probability: bestGotchi,
  forecasting: bestGotchi,
  sandbox: robotGotchi,
}

/** Neutral fake bars — no party names. */
const DEMO_CHART: PredictionResults = {
  visitorId: 'demo',
  surveyId: 'demo',
  prediction: {
    selected: 1,
    data: [
      {
        id: 1,
        value: 0.38,
        color: '#5b8fd4',
        names: {
          acr: { fr: 'A', en: 'A', es: 'A' },
          short: { fr: 'Option A', en: 'Option A', es: 'Opción A' },
          full: { fr: 'Option A', en: 'Option A', es: 'Opción A' },
        },
      },
      {
        id: 2,
        value: 0.27,
        color: '#e09a3a',
        names: {
          acr: { fr: 'B', en: 'B', es: 'B' },
          short: { fr: 'Option B', en: 'Option B', es: 'Opción B' },
          full: { fr: 'Option B', en: 'Option B', es: 'Opción B' },
        },
      },
      {
        id: 3,
        value: 0.2,
        color: '#6bbf6b',
        names: {
          acr: { fr: 'C', en: 'C', es: 'C' },
          short: { fr: 'Option C', en: 'Option C', es: 'Opción C' },
          full: { fr: 'Option C', en: 'Option C', es: 'Opción C' },
        },
      },
      {
        id: 4,
        value: 0.15,
        color: '#c26bd1',
        names: {
          acr: { fr: 'D', en: 'D', es: 'D' },
          short: { fr: 'Option D', en: 'Option D', es: 'Opción D' },
          full: { fr: 'Option D', en: 'Option D', es: 'Opción D' },
        },
      },
    ],
  },
}

function DemoInfoCard({ imageSrc, label }: { imageSrc: string; label: string }) {
  return (
    <div className="ConsentProgressDemo-hereCard" role="img" aria-label={label}>
      <img src={imageSrc} alt="" className="ConsentProgressDemo-hereCard-gotchi" />
      <span className="ConsentProgressDemo-hereCard-label">{label}</span>
    </div>
  )
}

function DemoStepThumb({ segment }: { segment: ProgressSegment }) {
  const { t } = useTranslation('Consent')
  if (segment.pageId === 'consent') {
    return <DemoInfoCard imageSrc={gotchiIcon} label={t('progressDemo.youAreHere')} />
  }
  if (segment.kind === 'questionnaire') {
    return <DemoInfoCard imageSrc={robotIcon} label={t('progressDemo.yourProfile')} />
  }
  if (segment.pageId === 'results') {
    return (
      <div className="EditionProgressBar-tooltip-predictionBars">
        <PredictionBars predictionResults={DEMO_CHART} interactive={false} />
      </div>
    )
  }
  const src = segment.pageId ? STEP_IMAGES[segment.pageId] : undefined
  if (!src) {
    return <DemoInfoCard imageSrc={robotIcon} label={t('progressDemo.yourProfile')} />
  }
  return (
    <div className="EditionProgressBar-tooltip-thumbWrap">
      <img
        src={src}
        alt=""
        className="EditionProgressBar-tooltip-thumb ConsentProgressDemo-stepThumb"
      />
    </div>
  )
}

export const ConsentProgressDemo = memo(function ConsentProgressDemo() {
  const { t, i18n } = useTranslation('Consent')
  const { t: tProgress } = useTranslation('EditionProgress')
  const { survey, questionsCount, questionsList, questionIds } = useContext(SurveyContext)
  const [expandedSegmentId, setExpandedSegmentId] = useState<string | null>(null)
  const [isTouchUi, setIsTouchUi] = useState(false)
  const lang = (i18n.language ?? 'fr').substring(0, 2)
  // Keep source placement (`top-start`) — do not let children default to `top` overrides.
  const [tippySource, tippyTarget] = useSingleton()

  useEffect(() => {
    const mq = window.matchMedia('(hover: none), (pointer: coarse)')
    const update = () => setIsTouchUi(mq.matches)
    update()
    mq.addEventListener('change', update)
    return () => mq.removeEventListener('change', update)
  }, [])

  const fallbackQuestionTotal = useMemo(() => {
    const fromList = questionsList?.length ?? 0
    const fromIds = questionIds?.length ?? 0
    const fromCount = questionsCount > 0 ? questionsCount : 0
    return Math.max(fromList, fromIds, fromCount)
  }, [questionsList, questionIds, questionsCount])

  const editionSurveys = useMemo(
    () => sortedCategorySurveys(getCategory(survey)),
    [survey],
  )

  const questionnaireLengthsByKey = useMemo(() => {
    const lengths: Record<string, number> = {}
    for (const ref of editionSurveys) {
      const count = categorySurveyQuestionCount(ref)
      if (count != null) {
        lengths[ref.key] = count
      }
    }
    // Fallback: current survey length when API has not yet enriched active_surveys.
    if (survey?.key && fallbackQuestionTotal > 0 && lengths[survey.key] == null) {
      lengths[survey.key] = fallbackQuestionTotal
    }
    return Object.keys(lengths).length ? lengths : undefined
  }, [editionSurveys, survey?.key, fallbackQuestionTotal])

  const surveyMetaByKey = useMemo(() => {
    const map = new Map<string, { name?: string; questionCount?: number }>()
    for (const ref of editionSurveys) {
      map.set(ref.key, {
        name: categorySurveyDisplayName(ref, lang),
        questionCount: categorySurveyQuestionCount(ref),
      })
    }
    return map
  }, [editionSurveys, lang])

  const segments = useMemo(() => {
    const built = buildEditionProgressSegments({
      category: getCategory(survey),
      currentSurvey: survey,
      step: SurveyStep.Answer,
      hasConsented: false,
      surveyIsCompleted: false,
      currentPosition: 0,
      questionsCount: fallbackQuestionTotal,
      isIntegrated: false,
      hasVisitedRectify: true,
      resultVariant: 'final',
      questionnaireLengthsByKey,
    })
    return built.map((seg) => {
      const meta = seg.surveyKey ? surveyMetaByKey.get(seg.surveyKey) : undefined
      const total =
        meta?.questionCount ??
        (seg.surveyKey ? questionnaireLengthsByKey?.[seg.surveyKey] : undefined) ??
        (seg.kind === 'questionnaire' ? fallbackQuestionTotal || undefined : undefined)
      return {
        ...seg,
        status: (seg.pageId === 'consent' ? 'current' : 'upcoming') as ProgressSegment['status'],
        fillRatio: 1,
        visibleQuestionCount: seg.kind === 'questionnaire' ? total : undefined,
        totalQuestionCount: seg.kind === 'questionnaire' ? total : undefined,
      }
    })
  }, [survey, fallbackQuestionTotal, questionnaireLengthsByKey, surveyMetaByKey])

  const segmentTitle = useCallback(
    (segment: ProgressSegment): string => {
      if (segment.kind === 'questionnaire') {
        const meta = segment.surveyKey ? surveyMetaByKey.get(segment.surveyKey) : undefined
        if (meta?.name) {
          return meta.name
        }
        return tProgress('questionnaire', {
          index: (segment.surveyIndex ?? 0) + 1,
          count: segment.surveyCount ?? 1,
        })
      }
      if (segment.pageId === 'results') {
        return segment.resultVariant === 'intermediate'
          ? tProgress('resultsIntermediate')
          : tProgress('resultsFinal')
      }
      return tProgress(segment.pageId ?? 'consent')
    },
    [surveyMetaByKey, tProgress],
  )

  const renderTooltip = useCallback(
    (segment: ProgressSegment) => {
      const title = segmentTitle(segment)
      const blurb = t(`progressDemo.step.${stepBlurbKey(segment)}`)
      const total = segment.totalQuestionCount

      return (
        <div className="EditionProgressBar-tooltip">
          <p className="EditionProgressBar-tooltip-title">{title}</p>
          <p className="EditionProgressBar-tooltip-status ConsentProgressDemo-stepBlurb">{blurb}</p>
          {segment.kind === 'questionnaire' && total != null && total > 0 && (
            <p className="EditionProgressBar-tooltip-body">
              {tProgress('questionsCount', { count: total })}
            </p>
          )}
          {segment.pageId === 'email' && (
            <p className="EditionProgressBar-tooltip-hint EditionProgressBar-tooltip-hint--green">
              {tProgress('emailNotLinked')}
            </p>
          )}
          <DemoStepThumb segment={segment} />
        </div>
      )
    },
    [segmentTitle, t, tProgress],
  )

  const tippyPopperOptions = {
    strategy: 'absolute' as const,
    modifiers: [
      {
        name: 'preventOverflow',
        options: {
          boundary: tippyBoundary(),
          padding: 8,
          altAxis: true,
          tether: false,
        },
      },
      // Consent demo: always open above the bar.
      { name: 'flip', enabled: false },
    ],
  }

  const intro = isTouchUi ? t('progressDemo.introMobile') : t('progressDemo.introDesktop')

  return (
    <DefiDatagotchiCallout className="ConsentProgressDemo">
      <p className="ConsentProgressDemo-intro">{intro}</p>
      <div className="ConsentProgressDemo-trackWrap">
        <Tippy
          singleton={tippySource}
          placement="top-start"
          interactive={false}
          trigger="mouseenter focus click"
          touch={true}
          appendTo={tippyBoundary}
          delay={[80, 40]}
          duration={[120, 80]}
          moveTransition="transform 0.12s ease-out"
          maxWidth={320}
          zIndex={200}
          popperOptions={tippyPopperOptions}
        />
        <div className="EditionProgressBar EditionProgressBar--demo" role="group" aria-label={tProgress('ariaLabel')}>
          {segments.map((segment, index) => {
            const expanded = expandedSegmentId === segment.id
            const isFirst = index === 0
            const isLast = index === segments.length - 1
            return (
              <div
                key={`${segment.id}-${segment.totalQuestionCount ?? 'x'}`}
                className={[
                  'EditionProgressBar-segSlot',
                  isFirst ? 'EditionProgressBar-segSlot--first' : '',
                  isLast ? 'EditionProgressBar-segSlot--last' : '',
                ]
                  .filter(Boolean)
                  .join(' ')}
                style={{
                  flexGrow: segment.widthPct,
                  zIndex: expanded ? segments.length + 2 : segments.length - index,
                }}
              >
                <Tippy
                  content={renderTooltip(segment)}
                  placement="top-start"
                  singleton={tippyTarget}
                  onShow={(instance: TippyInstance) => {
                    setExpandedSegmentId(segment.id)
                    instance.popperInstance?.update()
                  }}
                  onHide={() => {
                    setExpandedSegmentId((id) => (id === segment.id ? null : id))
                  }}
                >
                  <button
                    type="button"
                    className={[
                      'EditionProgressBar-segment',
                      `EditionProgressBar-segment--${segment.kind}`,
                      `EditionProgressBar-segment--${segment.status}`,
                      segmentTypeClass(segment),
                      isFirst ? 'EditionProgressBar-segment--first' : '',
                      isLast ? 'EditionProgressBar-segment--last' : '',
                      expanded ? 'EditionProgressBar-segment--expanded' : '',
                    ]
                      .filter(Boolean)
                      .join(' ')}
                    aria-label={segmentTitle(segment)}
                  >
                    <span className="EditionProgressBar-segment-track">
                      <span
                        className={[
                          'EditionProgressBar-segment-fill',
                          segment.fillRatio >= 1 ? 'EditionProgressBar-segment-fill--full' : '',
                        ]
                          .filter(Boolean)
                          .join(' ')}
                        style={{ width: `${Math.round(segment.fillRatio * 100)}%` }}
                      />
                    </span>
                    {segment.pageId === 'prediction' && (
                      <img
                        src={gotchiIcon}
                        alt=""
                        className="EditionProgressBar-segment-gotchi"
                        aria-hidden
                      />
                    )}
                  </button>
                </Tippy>
              </div>
            )
          })}
        </div>
      </div>
    </DefiDatagotchiCallout>
  )
})

export default ConsentProgressDemo
